import React, { useEffect, useState } from 'react';
import pathfinder from '@fiverr-private/pathfinder';
import { getContext } from '@fiverr-private/fiverr_context';
import { Button, IconButton } from '@fiverr-private/button';
import { I18n, translate } from '@fiverr-private/i18n-react';
import { ChevronLeftIcon } from '@fiverr-private/visuals';
import { triggerSignUp } from '@fiverr-private/identification_triggers';
import { Divider } from '@fiverr-private/layout_components';
import { sendBigQueryEvent } from '../../../../utils/bigQuery';
import { sendMixPanelEvent } from '../../../../utils/mixPanel';
import { BIGQUERY_SIGN_UP_EVENT, LINKS } from '../constants';
import { useLogoMakerContext } from '../../../context/LogoMakerContext';
import { useHeaderContext } from '../../../context/HeaderContext';
import { reportLogoMakerClick } from '../utils';

export interface LogoMakerBackButtonProps {
    /** When set, skips referrer/history checks (e.g. Storybook). */
    visible?: boolean;
    onClick?: () => void;
}

export const LogoMakerBackButton = ({ visible: visibleProp, onClick }: LogoMakerBackButtonProps) => {
    const { showBackButton, isTransparent } = useLogoMakerContext();
    const [computedVisible, setComputedVisible] = useState(false);

    useEffect(() => {
        if (visibleProp !== undefined) {
            return;
        }
        const { referrer } = getContext();
        const history = typeof window !== 'undefined' ? window.history : undefined;
        setComputedVisible(Boolean(showBackButton && referrer && history && history.length > 1));
    }, [showBackButton, visibleProp]);

    const effectiveVisible = visibleProp ?? computedVisible;

    if (!effectiveVisible) {
        return null;
    }

    const handleClick = onClick ?? (() => window.history.back());

    const colorScheme = isTransparent ? 'white' : undefined;

    return (
        <>
            <IconButton
                aria-label={translate<string>('header.logo_maker.go_back')}
                intent="secondary"
                variant="ghost"
                colorScheme={colorScheme}
                onClick={handleClick}
            >
                <ChevronLeftIcon size="lg" />
            </IconButton>
            <Divider
                borderColor={isTransparent ? 'white' : 'border'}
                direction="vertical"
                height="24px"
                marginRight="4"
            />
        </>
    );
};

export const JoinButton = () => {
    const [loading, setLoading] = useState(false);
    const [isHovered, setIsHovered] = useState(false);
    const { isTransparent } = useLogoMakerContext();

    const path = pathfinder('join', {}, { query: { source: 'top_nav' } });

    const handleClick = async (event) => {
        sendMixPanelEvent({
            eventName: 'Header join button clicked',
            eventData: {},
        });
        sendBigQueryEvent(BIGQUERY_SIGN_UP_EVENT);

        try {
            event.preventDefault();
            setLoading(true);

            await triggerSignUp({
                modalOptions: { source: 'header_join_link' },
            });
        } catch {
            window.location.assign(path);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Button
            rel="nofollow"
            onClick={handleClick}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            href={path}
            intent="primary"
            variant={isHovered ? 'filled' : 'outline'}
            size="md"
            loading={loading}
            colorScheme={isTransparent ? 'white' : 'business'}
        >
            <I18n k="header.join" />
        </Button>
    );
};

export const MakeYourLogoButton = () => {
    const path = pathfinder('logo_maker_brief_page_logo_maker_brief', {}, { query: { source: 'header_nav' } });

    return (
        <Button rel="nofollow" href={path} intent="primary" variant="filled" size="md">
            <I18n k="header.your_new_logo" />
        </Button>
    );
};

export const MakeNewLogoButton = () => {
    const { user } = useHeaderContext();
    const path = pathfinder('logo_maker_brief_page_logo_maker_brief', {}, { query: { source: 'header_nav' } });

    const handleClick = () => {
        reportLogoMakerClick(user.id, LINKS.NEW_LOGO);
    };

    return (
        <Button
            variant="outline"
            href={path}
            onClick={handleClick}
            target="_blank"
            data-click="logo-maker-header-new-logo-link"
        >
            <I18n k="header.new_logo" />
        </Button>
    );
};
